/**
 * Mixed Validation Mode
 *
 * Combines simple per-widget answer matching with expression-based validation.
 *
 * Semantics:
 *   - Every widget with a declared `answer` must match (simple equality).
 *   - The boolean `expression` must evaluate true with user inputs in scope.
 *   - Overall isCorrect = simple.isCorrect AND expression result.
 *
 * Score formula (per project decision):
 *   score = (simpleCorrect + (expressionPassed ? 1 : 0)) / (simpleCount + 1)
 *
 * Per-widget detail merging:
 *   - Widget that has an answer AND appears in expression → AND of both verdicts.
 *   - Widget with only an answer → simple verdict.
 *   - Widget that is only an expression free variable → expression verdict.
 */
import type { ValidatedExerciseJSON } from '../transformers/json-schema';
import { ValidationResult, WidgetValidation } from './types';
import { validateSimple } from './simple';
import { validateExpression } from './expression';

/**
 * Validate using mixed mode (simple answers + expression).
 *
 * @param exercise - The exercise JSON (with validation present)
 * @param userInputs - User's answers keyed by widget ID
 * @returns Validation result
 */
export function validateMixed(
  exercise: ValidatedExerciseJSON,
  userInputs: Record<string, string | number | string[][] | number[]>
): ValidationResult {
  const answers = exercise.validation.answers ?? {};

  // Run simple over the answers subset. validateSimple already iterates `answers`.
  const simpleResult = validateSimple(exercise, userInputs);

  // Run expression over scalar inputs only (matcher/visual-fraction values
  // don't participate in arithmetic expressions).
  const scalarInputs: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(userInputs)) {
    if (typeof v === 'string' || typeof v === 'number') scalarInputs[k] = v;
  }
  const exprResult = validateExpression(exercise, scalarInputs);

  // Merge per-widget details. For each widget we expose:
  //   - `simpleCorrect`  : set when widget has an `answer` in validation (local equality check)
  //   - `expressionCorrect`: set when widget appears in the expression (global expr verdict)
  //   - `isCorrect`      : AND of whichever verdicts apply (aggregate, for backward-compat)
  //
  // We key off `Object.keys(answers)` rather than `simpleResult.details`, because
  // validateSimple inserts filler details (isCorrect=true) for every input the user
  // supplied — including pure expression free vars — which would be misread as a
  // simple verdict here.
  const details: Record<string, WidgetValidation> = {};
  const answerKeys = new Set(Object.keys(answers));

  for (const k of answerKeys) {
    const simpleDetail = simpleResult.details[k];
    if (!simpleDetail) continue; // defensive: validateSimple should always emit a detail per answer
    const inExpr = k in exprResult.details;
    details[k] = {
      ...simpleDetail,
      simpleCorrect: simpleDetail.isCorrect,
      ...(inExpr && { expressionCorrect: exprResult.isCorrect }),
      isCorrect: inExpr ? simpleDetail.isCorrect && exprResult.isCorrect : simpleDetail.isCorrect,
    };
  }
  for (const [k, detail] of Object.entries(exprResult.details)) {
    if (!(k in details)) {
      details[k] = {
        ...detail,
        expressionCorrect: exprResult.isCorrect,
      };
    }
  }

  // Score per agreed formula.
  const simpleCount = Object.keys(answers).length;
  let simpleCorrect = 0;
  for (const k of Object.keys(answers)) {
    if (simpleResult.details[k]?.isCorrect) simpleCorrect++;
  }
  const score = (simpleCorrect + (exprResult.isCorrect ? 1 : 0)) / (simpleCount + 1);

  const isCorrect = simpleResult.isCorrect && exprResult.isCorrect;

  const result: ValidationResult = {
    isCorrect,
    details,
    score,
    expression: exercise.validation.expression,
    scope: exprResult.scope,
  };

  // Propagate the first useful error if anything failed (expression first, simple as fallback).
  const propagatedError = exprResult.error ?? simpleResult.error;
  if (!isCorrect && propagatedError) {
    result.error = propagatedError;
  }

  return result;
}
